import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { 
      name: 'Solutions', 
      path: '/solutions',
      dropdown: [
        { name: 'Residential Solar', path: '/solutions/residential-solar' },
        { name: 'Commercial Solar', path: '/solutions/commercial-solar' },
        { name: 'Housing Society Solar', path: '/solutions/housing-society-solar' },
        { name: 'On-Grid Solar', path: '/solutions/on-grid-solar' },
        { name: 'Off-Grid Solar', path: '/solutions/off-grid-solar' },
      ]
    },
    { name: 'Calculator', path: '/savings-calculator' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 backdrop-blur-xl shadow-md' : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3 group" data-testid="logo-link">
            <div>
              <img
                src={process.env.PUBLIC_URL + '/Amigos Solar Logo.png'}
                alt="Amigos Solar"
                className="h-16 md:h-20 object-contain"
              />
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              item.dropdown ? (
                <div key={item.name} className="relative group">
                  <button className={`text-stone-700 hover:text-primary font-medium transition-colors relative pb-2 ${
                    location.pathname.startsWith(item.path) && item.path !== '/' ? 'text-primary' : ''
                  }`} data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}>
                    {item.name}
                    {location.pathname.startsWith(item.path) && item.path !== '/' && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-secondary rounded-full" />
                    )}
                  </button>
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-hover opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 py-2">
                    {item.dropdown.map((sub) => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        className="block px-6 py-3 text-stone-700 hover:bg-stone-50 hover:text-primary transition-colors"
                        data-testid={`nav-dropdown-${sub.name.toLowerCase().replace(/ /g, '-')}`}
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-stone-700 hover:text-primary font-medium transition-colors relative pb-2 ${
                    location.pathname === item.path ? 'text-primary' : ''
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  {item.name}
                  {location.pathname === item.path && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-secondary rounded-full" />
                  )}
                </Link>
              )
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              className="bg-secondary text-black hover:brightness-95 rounded-full px-6 py-3 font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              data-testid="cta-quote-button"
            >
              Get Free Solar Quote
            </Link>
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 top-20 bg-white z-40 lg:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 space-y-4">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.dropdown ? (
                    <div className="space-y-2">
                      <p className="font-semibold text-primary">{item.name}</p>
                      {item.dropdown.map((sub) => (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block pl-4 py-2 text-stone-700 hover:text-primary"
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block py-2 text-lg text-stone-700 hover:text-primary font-medium"
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block bg-primary text-white text-center rounded-full px-6 py-3 font-medium mt-8"
              >
                Get Free Solar Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};